<template>
  <form-base :header="header" :is-loading="loading" :teleport-target="dialogTeleportTarget">
    <template #form>
      <div><language-select /></div>

      <v-form ref="form">
        <v-container class="d-flex flex-column gap-4">
          <div class="d-flex flex-row gap-4">
            <div class="full-width">
              <text-field
                v-if="editMode"
                :required="true"
                v-model="fields.firstName"
                :rules="$validator.rules.required(firstNameLabel)"
                :label="firstNameLabel"></text-field>
              <div class="v-text-field pt-0" v-else>
                <v-label>
                  <strong>{{ firstNameLabel }}</strong>
                </v-label>
                <p>
                  <v-label>
                    {{ $me.firstName }}
                  </v-label>
                </p>
              </div>
            </div>

            <div class="full-width">
              <text-field
                v-if="editMode"
                :required="true"
                v-model="fields.lastName"
                :rules="$validator.rules.required(lastNameLabel)"
                :label="lastNameLabel"></text-field>
              <div class="v-text-field pt-0" v-else>
                <v-label>
                  <strong>{{ lastNameLabel }}</strong>
                </v-label>
                <p>
                  <v-label>
                    {{ $me.lastName }}
                  </v-label>
                </p>
              </div>
            </div>
          </div>
          <div class="full-width">
            <phone-number-field
              v-if="editMode"
              margin-top="-7px"
              v-model="fields.phone"
              :validator="$validator"
              :label="phoneLabel"></phone-number-field>
            <div class="v-text-field pt-0" v-else>
              <v-label>
                <strong>{{ phoneLabel }}</strong>
              </v-label>
              <p>
                <v-label>
                  {{ $me.phone ? formatPhoneNumber($me.phone) : '-' }}
                </v-label>
              </p>
            </div>
          </div>

          <div class="d-flex flex-row gap-4" v-if="!editMode">
            <div class="v-text-field full-width">
              <v-label>
                <strong>{{ emailLabel }}</strong>
              </v-label>
              <p>
                <v-label>
                  {{ $me.email }}
                </v-label>
              </p>
            </div>

            <div class="v-text-field full-width">
              <v-label>
                <strong>{{ roleLabel }}</strong>
              </v-label>
              <p>
                <v-label>
                  {{ userRoleToText($me.role) }}
                </v-label>
              </p>
            </div>
          </div>

          <div v-if="isWarehouseListVisible && !editMode" class="d-flex flex-row gap-4">
            <div class="v-text-field pt-0">
              <v-label>
                <strong>{{ warehouseListLabel }}</strong>
              </v-label>
              <p class="mt-3">
                <v-chip
                  class="mr-2 mb-2"
                  size="small"
                  v-for="warehouse in warehouses"
                  :key="warehouse.id">
                  {{ warehouse.name }}
                </v-chip>
              </p>
            </div>
          </div>
          <v-spacer class="my-2"></v-spacer>
        </v-container>
      </v-form>
    </template>
    <template #form-actions>
      <action-group
        @cancel="cancel"
        :hide-cancel="editMode"
        @confirm="submit"
        :hide-confirm="!editMode"
        confirm-icon="check"
        :confirm-label="$t('global.buttons.save')">
        <template #additional-actions>
          <v-btn
            class="ml-2"
            variant="outlined"
            @click="editMode = false"
            :color="editMode ? 'default' : 'secondary'"
            v-if="editMode">
            <v-icon size="small" class="mr-2">mdi-pencil-off</v-icon>
            {{ $t('account.forms.myAccountForm.buttons.cancelEdit') }}
          </v-btn>

          <template v-else>
            <secondary-button @click="editMode = true" class="ml-2">
              <v-icon size="small" class="mr-2">mdi-pencil</v-icon>
              {{ $t('global.buttons.edit') }}
            </secondary-button>
            <change-password-dialog>
              <template v-slot:activator="slotProps">
                <v-btn class="ml-3" v-bind="slotProps" variant="outlined">
                  <v-icon size="small" class="mr-2">mdi-key</v-icon>
                  {{ $t('account.forms.myAccountForm.buttons.changePassword') }}
                </v-btn>
              </template>
            </change-password-dialog>
          </template>
        </template>
      </action-group>
    </template>
  </form-base>
</template>

<script>
import { inject } from 'vue';
import {
  deepClone,
  formatPhoneNumber,
  isUserWarehouseRestricted,
  userRoleToText
} from '@nova/core';
import LanguageSelect from '@satellite/components/elements/selects/LanguageSelect';

/**
 * My Account Form
 * @displayName My Account Form
 */
export default {
  components: { LanguageSelect },
  props: {
    /**
     * Form Header
     */
    header: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      fields: {
        email: '',
        phone: '',
        firstName: '',
        lastName: ''
      },
      formatPhoneNumber,
      editMode: false,
      loading: false,
      userRoleToText,
      warehouses: []
    };
  },
  computed: {
    firstNameLabel() {
      return this.$t('account.forms.myAccountForm.labels.firstName');
    },
    lastNameLabel() {
      return this.$t('account.forms.myAccountForm.labels.lastName');
    },
    phoneLabel() {
      return this.$t('account.forms.myAccountForm.labels.phone');
    },
    emailLabel() {
      return this.$t('account.forms.myAccountForm.labels.email');
    },
    roleLabel() {
      return this.$t('account.forms.myAccountForm.labels.role');
    },
    warehouseListLabel() {
      return this.$t('account.forms.myAccountForm.labels.warehouseList');
    },
    isUserWarehouseRestricted() {
      return isUserWarehouseRestricted(this.$me);
    },

    isWarehouseListVisible() {
      return this.isUserWarehouseRestricted && this.warehouses.length > 0;
    }
  },
  methods: {
    /**
     * Submit the form
     * @public
     * @returns {Promise<void>}
     */
    async submit() {
      if (await this.$isFormValid(this.$refs.form)) {
        this.loading = true;
        const newFields = deepClone(this.fields);
        newFields.phone = this.parsePhoneNumber(newFields.phone);

        try {
          const res = await axios.patch(`user/${this.$me.id}`, newFields);
          if (res?.data?.data) {
            await this.$store.dispatch('Auth/getMe');
            this.$emit('saved', res.data.data);
          }
        } catch (e) {
          this.notify(
            this.$t('account.forms.myAccountForm.messages.failedToUpdateUserData'),
            'error'
          );
        } finally {
          this.$emit('close');
        }

        this.loading = false;
      }
    },
    parsePhoneNumber(phone) {
      return phone?.replace(/(\()|(\))|( )|(-)/g, '').trim() || null;
    },
    cancel() {
      /**
       * Emits the close event
       * @event close
       */
      this.$emit('close');
    },
    async fetchWarehouseNames() {
      const params = {
        s: {
          id: {
            $in: this.$me.warehouseAccessList
          }
        }
      };
      if (this.isUserWarehouseRestricted) {
        const warehouses = await this.services.warehouse.getWarehouses(params, {
          fields: ['name']
        });

        if (warehouses?.length > 0) {
          this.warehouses = warehouses;
        }
      }
    }
  },
  async mounted() {
    this.fields = {
      firstName: this.$me.firstName,
      lastName: this.$me.lastName,
      phone: this.$me.phone
    };

    await this.fetchWarehouseNames();
  },
  setup() {
    const dialogTeleportTarget = inject('dialogActionsRef');
    return { dialogTeleportTarget };
  }
};
</script>
